import * as React from "react";
import {
  addProductInputSchema,
  FileType,
  IAddProductInput,
  ICategory,
  ICreateFileResponse,
  IProduct,
  ISeller,
  type ApiResponse,
} from "muminsmart-utils";
import { Stepper } from "@/app/components/common/Stepper";
import ProductDetail from "@/app/components/seller/product/ProductDetails";
import { philosopher } from "@/lib/fonts";
import {
  FormProvider,
  useForm,
  useFormState,
  type FieldErrors,
  type Resolver,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import ProductVariants from "@/app/components/seller/product/ProductVariants";
import ProductVariantSummary from "./ProductVariantSummary";
import ProductDetailsPreview from "@/app/components/product/ProductDetails";
import { buildProductPreviewFromFormValues } from "@/app/components/product/utils/buildProductPreview";
import { useSession } from "@/contexts/AuthContext";
import { fetchUploadFile } from "@/lib/api/file";
import { toast } from "sonner";
import {
  useCreateProductMutation,
  useUpdateProductMutation,
} from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/constants";
import { useConfirm } from "@/hooks/use-confirm";
import ConfirmDialog from "../../common/ConfirmAlert";

interface ProductFormProps {
  categories: ICategory[];
  selectedProduct: IProduct | null;
}

const STEP_0_FIELDS = [
  "name",
  "description",
  "sellerId",
  "categoryIds",
  "subCategoryIds",
  "subSubCategoryIds",
  "location",
  "hsn",
  "gstRate",
  "ean",
] as const;

function isErrorPathRelevantForStep(
  path: string,
  currentStep: number,
): boolean {
  if (currentStep >= 2) {
    return true;
  }

  const rootField = path.split(".")[0] ?? path;

  if (currentStep === 0) {
    return STEP_0_FIELDS.includes(rootField as (typeof STEP_0_FIELDS)[number]);
  }

  return rootField === "variants";
}

function collectStepErrorMessages(
  errors: FieldErrors<IAddProductInput>,
  currentStep: number,
): string[] {
  const messages: string[] = [];
  const visited = new WeakSet<object>();

  const walk = (value: unknown, path = ""): void => {
    if (!value || typeof value !== "object") {
      return;
    }

    if (visited.has(value)) {
      return;
    }
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        walk(item, path ? `${path}.${index}` : String(index));
      });
      return;
    }

    const errorObject = value as Record<string, unknown>;
    const message = errorObject.message;

    if (
      typeof message === "string" &&
      message.trim().length > 0 &&
      path &&
      isErrorPathRelevantForStep(path, currentStep)
    ) {
      messages.push(message.trim());
    }

    for (const [key, nestedValue] of Object.entries(errorObject)) {
      if (key === "message" || key === "type" || key === "ref") {
        continue;
      }

      walk(nestedValue, path ? `${path}.${key}` : key);
    }
  };

  walk(errors);

  return Array.from(new Set(messages));
}

const uploadProductFileWithDedup = (
  file: File,
  uploadCache: Map<string, Promise<ApiResponse<ICreateFileResponse | null>>>,
): Promise<ApiResponse<ICreateFileResponse | null>> => {
  const signature = `${file.name}:${file.size}:${file.lastModified}`;
  const cachedUpload = uploadCache.get(signature);

  if (cachedUpload) {
    return cachedUpload;
  }

  const uploadPromise = fetchUploadFile(FileType.product, file);
  uploadCache.set(signature, uploadPromise);
  return uploadPromise;
};

const ProductForm: React.FunctionComponent<ProductFormProps> = ({
  categories,
  selectedProduct,
}) => {
  const { data: session, status } = useSession();
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const { isOpen, confirm, handleCancel, handleConfirm, title, description } =
    useConfirm();

  const [currentStep, setCurrentStep] = React.useState(0);
  const [seller, setSeller] = React.useState<ISeller>();
  const [productAlwaysAvailable, setProductAlwaysAvailable] =
    React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const [previewProduct, setPreviewProduct] = React.useState<IProduct | null>(
    null,
  );
  const allowNavigationRef = React.useRef(false);
  const isConfirmingNavigationRef = React.useRef(false);
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const isEditMode = !!selectedProduct?.id;
  const schemaForResolver = addProductInputSchema as unknown as Parameters<
    typeof zodResolver
  >[0];

  const toFormData = React.useCallback(
    (product: IProduct): IAddProductInput => {
      const sellerIdFromProduct =
        product && typeof product === "object" && "seller" in product
          ? ((product as { seller?: { id?: string } }).seller?.id ?? "")
          : "";

      const categoryIds = product.categories.map((category) => category.id);
      const subCategoryIds = product.subCategories.map(
        (subCategory) => subCategory.id,
      );
      const subSubCategoryIds = product.subSubCategories.map(
        (subSubCategory) => subSubCategory.id,
      );

      return {
        name: product.name,
        description: product.description,
        sellerId: sellerIdFromProduct,
        categoryIds,
        subCategoryIds,
        subSubCategoryIds,
        location: product.location ?? "",
        hsn: product.hsn ?? "",
        gstRate: product.gstRate ?? 0,
        ean: product.ean ?? "",
        variants: (product.variants || []).map((variant) => ({
          variantAttributes: (variant.variantAttributes || []).map(
            (attribute) => ({
              attributeId: attribute.attributeId,
              value: attribute.value,
            }),
          ),
          bulkPrices: (variant.bulkPrices || []).map((bulkPrice) => ({
            quantity: Number(bulkPrice.quantity ?? 1),
            price: Number(bulkPrice.price ?? 0),
          })),
          price: Number(variant.price ?? 0),
          weight: Number(variant.weight ?? 0),
          discountedPrice: Number(variant.discountedPrice ?? 0),
          sku: variant.sku,
          stock: Number(variant.stock ?? 0),
          featuredImage: variant.featuredImage,
          additionalImages: variant.additionalImages || [],
          isActive: variant.isActive ?? true,
          alwaysAvailable: variant.alwaysAvailable ?? false,
        })),
      };
    },
    [],
  );

  const getProductAlwaysAvailableFromVariants = React.useCallback(
    (variants: IAddProductInput["variants"]): boolean => {
      if (!variants.length) {
        return false;
      }

      return variants.every((variant) => variant.alwaysAvailable === true);
    },
    [],
  );

  const form = useForm<IAddProductInput>({
    resolver: zodResolver(
      schemaForResolver,
    ) as unknown as Resolver<IAddProductInput>,
    defaultValues: {
      name: "",
      description: "",
      sellerId: "",
      categoryIds: [],
      subCategoryIds: [],
      subSubCategoryIds: [],
      variants: [],
      location: "",
      hsn: "",
      gstRate: 0,
      ean: "",
    },
    mode: "onChange",
  });
  const { errors } = useFormState({ control: form.control });
  const hasUnsavedChanges = form.formState.isDirty && !loading;

  // Set the sellerId once the session is available
  React.useEffect(() => {
    if (status === "authenticated" && session?.user?.sellers && !isEditMode) {
      form.setValue("sellerId", session.user.sellers[0]?.id || "");
      setSeller(session.user.sellers[0] || null);
    }
  }, [session, status, form, isEditMode]);

  React.useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    const formData = toFormData(selectedProduct);
    form.reset(formData);
    setProductAlwaysAvailable(
      getProductAlwaysAvailableFromVariants(formData.variants),
    );
  }, [
    form,
    getProductAlwaysAvailableFromVariants,
    selectedProduct,
    toFormData,
  ]);

  const handleProductAlwaysAvailableChange = React.useCallback(
    (checked: boolean) => {
      setProductAlwaysAvailable(checked);

      const currentVariants = form.getValues("variants") ?? [];
      currentVariants.forEach((_, variantIndex) => {
        form.setValue(`variants.${variantIndex}.alwaysAvailable`, checked, {
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    },
    [form],
  );

  const steps = [
    {
      title: "Product Details",
      component: (
        <ProductDetail
          key="product-detail"
          categories={categories}
          productAlwaysAvailable={productAlwaysAvailable}
          onProductAlwaysAvailableChange={handleProductAlwaysAvailableChange}
        />
      ),
    },
    {
      title: "Product Variants",
      component: (
        <ProductVariants
          key="product-variants"
          categories={categories}
          productAlwaysAvailable={productAlwaysAvailable}
          selectedProduct={selectedProduct}
        />
      ),
    },
    {
      title: "Summary",
      component: (
        <ProductVariantSummary
          key="product-variant-summary"
          categories={categories}
          selectedProduct={selectedProduct}
          onSeePreview={() => {
            const values = form.getValues();

            if (!values.variants?.length) {
              toast.error("Please add at least one variant to preview");
              return;
            }

            const preview = buildProductPreviewFromFormValues({
              values,
              categories,
              selectedProduct,
              seller,
              fallbackSeller: session?.user?.sellers?.[0] as ISeller | undefined,
            });

            setPreviewProduct(preview);
            setShowPreview(true);
          }}
        />
      ),
    },
  ];

  React.useEffect(() => {
    if (selectedProduct?.seller) {
      setSeller(selectedProduct.seller);
    }
  }, [selectedProduct]);

  const confirmDiscardChanges = React.useCallback(async () => {
    if (allowNavigationRef.current) {
      return true;
    }

    const confirmed = await confirm({
      title: "Discard changes?",
      description:
        "If you leave now, your unsaved product changes will be lost.",
    });

    if (confirmed) {
      allowNavigationRef.current = true;
    }

    return confirmed;
  }, [confirm]);

  React.useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const message =
      "You have unsaved product changes. Are you sure you want to leave this page?";

    const confirmLeave = () => {
      if (allowNavigationRef.current) {
        return true;
      }

      const confirmed = window.confirm(message);
      if (confirmed) {
        allowNavigationRef.current = true;
      }
      return confirmed;
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!confirmLeave()) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    const handlePopState = () => {
      if (allowNavigationRef.current) {
        return;
      }

      // Immediately restore current state, then ask with app dialog.
      window.history.pushState(null, "", window.location.href);

      if (isConfirmingNavigationRef.current) {
        return;
      }

      isConfirmingNavigationRef.current = true;
      void (async () => {
        const confirmed = await confirmDiscardChanges();
        if (confirmed) {
          navigate(-1);
        }
        isConfirmingNavigationRef.current = false;
      })();
    };

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest("a[href]") as HTMLAnchorElement | null;

      if (!link || link.target === "_blank" || link.hasAttribute("download")) {
        return;
      }

      const nextUrl = new URL(link.href, window.location.href);
      if (nextUrl.origin !== window.location.origin) {
        return;
      }

      const currentUrl = new URL(window.location.href);
      if (
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash
      ) {
        return;
      }

      if (!confirmLeave()) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Add a guard entry so browser back triggers popstate confirmation.
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [confirmDiscardChanges, hasUnsavedChanges, navigate]);

  const next = async () => {
    let valid = false;
    switch (currentStep) {
      case 0:
        valid = await form.trigger([...STEP_0_FIELDS]);
        if (valid) {
          form.clearErrors([...STEP_0_FIELDS]);
        }
        break;
      case 1:
        // No required fields in variants step for now
        valid = await form.trigger(["variants"]);
        break;
      default:
        break;
    }

    if (valid) setCurrentStep((s) => s + 1);
  };

  const prev = async () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    } else {
      const confirmed = await confirmDiscardChanges();

      if (confirmed) {
        navigate(-1);
      }
    }
  };

  const onSubmit = async (data: IAddProductInput) => {
    setLoading(true);

    try {
      const fileUploadCache = new Map<
        string,
        Promise<ApiResponse<ICreateFileResponse | null>>
      >();

      for await (const variant of data.variants) {
        if (variant.featuredImage instanceof File) {
          const uploadedFeaturedImage = await uploadProductFileWithDedup(
            variant.featuredImage,
            fileUploadCache,
          );
          const { success, data } = uploadedFeaturedImage;
          if (success && data) {
            variant.featuredImage = data.key;
          } else {
            toast.error("Failed to upload featured image for a variant");
            return;
          }
        }

        const uploadedAdditionalImages = await Promise.all(
          variant.additionalImages.map((file) => {
            if (file instanceof File) {
              return uploadProductFileWithDedup(file, fileUploadCache);
            }

            if (typeof file === "string" && file.trim().length > 0) {
              return Promise.resolve({
                success: true,
                data: { key: file },
                message: "Existing image kept",
              });
            }

            return Promise.resolve({
              success: false,
              data: { key: "invalid-key" },
              message: "Invalid file",
            });
          }),
        );

        const additionalImageKeys: string[] = [];
        for (const uploadResult of uploadedAdditionalImages) {
          const { success, data, message } = uploadResult;
          if (success && data) {
            additionalImageKeys.push(data.key);
          } else {
            toast.error(message);
            return;
          }
        }
        variant.additionalImages = additionalImageKeys;
      }

      if (isEditMode) {
        await updateProduct({
          id: selectedProduct.id,
          data,
        }).unwrap();
      } else {
        await createProduct(data).unwrap();
      }

      toast.success(
        isEditMode
          ? "Product updated successfully"
          : "Product created successfully",
      );

      const targetPath = routes.seller.myShop.index;
      allowNavigationRef.current = true;
      navigate(targetPath);

      // Fallback in case app-router navigation is interrupted by local guards.
      setTimeout(() => {
        if (window.location.pathname !== targetPath) {
          window.location.assign(targetPath);
        }
      }, 250);
    } catch (error: unknown) {
      const message = getRtkQueryErrorMessage(error, "Something went wrong");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const preventNativeSubmit: React.FormEventHandler<HTMLFormElement> = (
    event,
  ) => {
    event.preventDefault();
  };

  const formErrorMessages = React.useMemo(
    () => collectStepErrorMessages(errors, currentStep),
    [currentStep, errors],
  );

  if (showPreview && previewProduct) {
    return (
      <ProductDetailsPreview
        product={previewProduct}
        onSeePreview={() => {
          setShowPreview(false);
          setPreviewProduct(null);
        }}
        isPreview={true}
      />
    );
  }

  return (
    <div>
      <h1
        className={`text-center text-secondary text-3xl font-semibold ${philosopher.className} mb-3`}
      >
        <span>{isEditMode ? "Edit" : "Add"}</span>&nbsp;
        <span className="text-primary">Product</span>
      </h1>
      {/* <p className="text-[#6f6f6f] text-center text-sm mt-2 mb-10">
          Step into the Bohra marketplace as a seller
        </p> */}

      <div className="px-4">
        <Stepper steps={steps} currentStep={currentStep} />
      </div>
      <div className="p-6 mt-4 rounded-3xl bg-white">
        <FormProvider {...form}>
          <form onSubmit={preventNativeSubmit} className="space-y-6">
            {formErrorMessages.length > 0 ? (
              <div className="rounded-2xl border border-red-300 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-700">
                  Please fix the following errors:
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700">
                  {formErrorMessages.map((message, index) => (
                    <li key={`${message}-${index}`}>{message}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {steps[currentStep]["component"]}

            <div className="flex mt-6 gap-2">
              <Button
                type="button"
                size="lg"
                className="flex-1 bg-muted rounded-full"
                onClick={() => void prev()}
              >
                Back
              </Button>
              {currentStep === steps.length - 1 ? (
                <Button
                  type="button"
                  size="lg"
                  className="flex-1 bg-primary rounded-full"
                  onClick={() => void form.handleSubmit(onSubmit)()}
                  disabled={loading}
                >
                  {loading
                    ? isEditMode
                      ? "Saving Changes..."
                      : "Submitting..."
                    : isEditMode
                      ? "Save Changes"
                      : "Submit"}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="lg"
                  className="flex-1 bg-primary rounded-full"
                  onClick={next}
                >
                  Continue
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
      <ConfirmDialog
        isOpen={isOpen}
        title={title}
        description={description}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default ProductForm;
